import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  TextField,
  Button,
  Stack,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  InputAdornment,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";

const PAGE_SIZE = 25;
const POLL_MS = 4000;

function formatWhen(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const AUDIENCE_LABELS = { public: "Website", student: "Student", parent: "Parent", tutor: "Tutor" };

const STATUS_LABELS = {
  bot: "Bot",
  queued_for_human: "Waiting for team",
  assigned: "Assigned",
  closed: "Closed",
};

// Tabs: value sent as ?status=, label, key in the API's `counts`.
const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "queued_for_human", label: "Waiting" },
  { value: "assigned", label: "Assigned" },
  { value: "escalated", label: "Escalated" },
  { value: "bot", label: "Bot only" },
  { value: "closed", label: "Closed" },
];

const SENDER_STYLES = {
  visitor: { label: "Visitor", align: "flex-start", bgcolor: "grey.200", color: "text.primary" },
  bot: { label: "Bot", align: "flex-start", bgcolor: "#e3f2fd", color: "text.primary" },
  admin: { label: "Team", align: "flex-end", bgcolor: "primary.main", color: "primary.contrastText" },
  system: { label: "System", align: "center", bgcolor: "transparent", color: "text.secondary" },
};

function statusColor(status) {
  if (status === "queued_for_human") return "warning";
  if (status === "assigned") return "success";
  if (status === "closed") return "default";
  return "info";
}

export default function SupportChatInbox() {
  // /admin/support-chat/:conversationId (linked from the escalation email) opens that chat.
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const routeId = Number(conversationId) > 0 ? Number(conversationId) : null;
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [selectedId, setSelectedId] = useState(routeId);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [hours, setHours] = useState(null);
  const [error, setError] = useState("");
  const pollInFlightRef = useRef(false);

  // Filters. "" audience = website chats + escalated portal chats.
  const [audience, setAudience] = useState("");
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(false);

  // Latest values for the polling timer without re-creating it.
  const queryRef = useRef({});
  queryRef.current = { audience, status, search, includeEmpty, page };
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const loadList = useCallback(async (opts = { initial: false }) => {
    if (pollInFlightRef.current && !opts.initial) {
      return;
    }
    pollInFlightRef.current = true;
    if (opts.initial) {
      setLoadingList(true);
    } else {
      setRefreshing(true);
    }
    const q = queryRef.current;
    try {
      // Only the current page; newest activity first (sorted on the server).
      const res = await api.get("admin/support-chat/conversations", {
        params: {
          status: q.status,
          page: q.page,
          per_page: PAGE_SIZE,
          ...(q.audience ? { audience: q.audience } : {}),
          ...(q.search ? { search: q.search } : {}),
          ...(q.includeEmpty ? { include_empty: 1 } : {}),
        },
        timeout: 15000,
      });
      const payload = Array.isArray(res?.data?.data) ? res.data.data : [];
      const meta = res?.data?.meta || {};
      const last = Math.max(1, Number(meta.last_page) || 1);
      // The page shrank under us (chats closed/filtered out): step back to the last real page.
      if (payload.length === 0 && q.page > last) {
        queryRef.current = { ...queryRef.current, page: last };
        setPage(last);
        pollInFlightRef.current = false;
        return loadList(opts);
      }
      setConversations(payload);
      setTotal(meta.total ?? payload.length);
      setLastPage(last);
      setCounts(res?.data?.counts || null);
      setHours(res?.data?.support_hours || null);
      if (payload.length > 0 && !selectedIdRef.current) {
        setSelectedId(payload[0].id);
      }
      // Clear stale error banner after a successful refresh.
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load support chats.");
    } finally {
      pollInFlightRef.current = false;
      if (opts.initial) {
        setLoadingList(false);
      } else {
        setRefreshing(false);
      }
    }
  }, []);

  const loadDetail = async (id, opts = { initial: false }) => {
    if (!id) return;
    if (opts.initial) {
      setLoadingDetail(true);
    }
    try {
      const res = await api.get(`admin/support-chat/conversations/${id}`);
      setDetail(res?.data?.data || null);
      setHours(res?.data?.support_hours || hours);
      // Clear stale error banner after a successful detail fetch.
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load chat details.");
    } finally {
      if (opts.initial) {
        setLoadingDetail(false);
      }
    }
  };

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Back to page 1 whenever a filter or the search changes.
  useEffect(() => {
    setPage(1);
    queryRef.current = { ...queryRef.current, page: 1 };
    loadList({ initial: true });
  }, [audience, status, search, includeEmpty, loadList]);

  // Follow a new deep link while the page is already open.
  useEffect(() => {
    if (routeId && routeId !== selectedId) setSelectedId(routeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Keep the URL in step with the open chat so it can be shared or reloaded.
  useEffect(() => {
    if (selectedId && selectedId !== routeId) {
      navigate(`/admin/support-chat/${selectedId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId, { initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    const id = setInterval(async () => {
      // Background refresh (do not block UI / selection)
      await loadList({ initial: false });
      if (selectedIdRef.current) {
        await loadDetail(selectedIdRef.current, { initial: false });
      }
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToPage = (p) => {
    if (p === page) return;
    setPage(p);
    queryRef.current = { ...queryRef.current, page: p };
    loadList({ initial: true });
  };

  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(page * PAGE_SIZE, total);

  // Messages oldest -> newest; jump to the bottom when a chat opens or a new message arrives.
  const messagesBoxRef = useRef(null);
  const sortedMessages = [...(detail?.messages || [])].sort((a, b) => a.id - b.id);
  const lastMessageId = sortedMessages.length ? sortedMessages[sortedMessages.length - 1].id : 0;
  useEffect(() => {
    const el = messagesBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [detail?.id, lastMessageId]);

  const assignToMe = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/assign`);
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to assign conversation.");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/reply`, {
        message: reply.trim(),
      });
      setReply("");
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const closeConversation = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`admin/support-chat/conversations/${selectedId}/close`);
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to close conversation.");
    } finally {
      setSaving(false);
    }
  };

  const renderMessage = (m) => {
    const style = SENDER_STYLES[m.sender_type] || SENDER_STYLES.visitor;
    if (m.sender_type === "system") {
      return (
        <Box key={m.id} sx={{ mb: 1.5, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}>
            {m.body} · {formatWhen(m.created_at)}
          </Typography>
        </Box>
      );
    }
    const label =
      m.sender_type === "admin" && detail?.assigned_admin?.name
        ? `Team · ${detail.assigned_admin.name}`
        : m.sender_type === "visitor"
          ? detail?.visitor_user?.name || detail?.visitor_name || style.label
          : style.label;
    return (
      <Box key={m.id} sx={{ mb: 1.5, display: "flex", justifyContent: style.align }}>
        <Box
          sx={{
            maxWidth: "85%",
            px: 1.2,
            py: 1,
            borderRadius: 2,
            bgcolor: style.bgcolor,
            color: style.color,
            border: m.sender_type === "bot" ? "1px solid #bbdefb" : "none",
          }}
        >
          <Typography variant="caption" sx={{ display: "block", opacity: 0.8, fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {m.body}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.3, opacity: 0.7 }}>
            {formatWhen(m.created_at)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Support Chat Inbox
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {hours?.open
          ? "Live support hours are open."
          : `Live support is outside working hours. ${hours?.label || ""}`}
      </Typography>
      {refreshing ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Refreshing...
        </Typography>
      ) : null}
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ width: { xs: "100%", md: 380 }, maxHeight: 760, overflow: "auto" }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Conversations {total ? `(${total})` : ""}
            </Typography>
            <Tabs
              value={status}
              onChange={(e, v) => {
                setStatus(v);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mt: 1, minHeight: 36, "& .MuiTab-root": { minHeight: 36, px: 1.2, textTransform: "none" } }}
            >
              {STATUS_TABS.map((t) => (
                <Tab
                  key={t.value}
                  value={t.value}
                  label={counts && counts[t.value] != null ? `${t.label} (${counts[t.value]})` : t.label}
                />
              ))}
            </Tabs>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={audience}
              onChange={(e, v) => {
                if (v === null) return;
                setAudience(v);
              }}
              sx={{ mt: 1, flexWrap: "wrap" }}
            >
              <ToggleButton value="">All</ToggleButton>
              <ToggleButton value="public">Website</ToggleButton>
              <ToggleButton value="student">Student</ToggleButton>
              <ToggleButton value="parent">Parent</ToggleButton>
              <ToggleButton value="tutor">Tutor</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              fullWidth
              size="small"
              placeholder="Search name, email, phone, #id or message"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ mt: 1.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch size="small" checked={includeEmpty} onChange={(e) => setIncludeEmpty(e.target.checked)} />
              }
              label={<Typography variant="caption">Show chats where the visitor never wrote</Typography>}
            />
          </Box>
          {loadingList ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <List disablePadding>
              {conversations.map((c) => (
                <React.Fragment key={c.id}>
                  <ListItemButton
                    selected={selectedId === c.id}
                    onClick={() => setSelectedId(c.id)}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <ListItemText
                      primary={`#${c.id} ${c.visitor_name || c.visitor_email || "Visitor"}`}
                      secondary={
                        <>
                          <Box component="span" sx={{ mt: 0.5, display: "block" }}>
                            <Chip
                              size="small"
                              color={statusColor(c.status)}
                              label={STATUS_LABELS[c.status] || c.status}
                              sx={{ mr: 1 }}
                            />
                            {c.audience && c.audience !== "public" ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={`${AUDIENCE_LABELS[c.audience]}${c.student_name ? ` · ${c.student_name}` : ""}`}
                                sx={{ mr: 1 }}
                              />
                            ) : null}
                            {c.escalation_reason === "safeguarding" ? (
                              <Chip size="small" color="error" label="SAFEGUARDING" />
                            ) : null}
                          </Box>
                          {c.last_message_preview ? (
                            <Typography variant="caption" display="block" noWrap sx={{ mt: 0.8 }}>
                              {(SENDER_STYLES[c.last_message_sender]?.label || "") + ": "}
                              {c.last_message_preview}
                            </Typography>
                          ) : null}
                          {c.escalation_reason ? (
                            <Typography variant="caption" display="block" color="warning.dark" noWrap>
                              {c.escalation_reason}
                            </Typography>
                          ) : null}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {c.visitor_messages_count ?? 0} visitor message(s) · {formatWhen(c.last_message_at || c.updated_at)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
              {conversations.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No conversations match these filters.
                  </Typography>
                </Box>
              ) : null}
              {total > 0 ? (
                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {firstRow}–{lastRow} of {total}
                  </Typography>
                  {lastPage > 1 ? (
                    <Pagination
                      size="small"
                      count={lastPage}
                      page={page}
                      onChange={(e, p) => goToPage(p)}
                      color="primary"
                      showFirstButton
                      showLastButton
                      siblingCount={0}
                    />
                  ) : null}
                </Box>
              ) : null}
            </List>
          )}
        </Paper>

        <Paper sx={{ flex: 1, minHeight: 700, display: "flex", flexDirection: "column" }}>
          {!selectedId ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Select a conversation from the left.
              </Typography>
            </Box>
          ) : loadingDetail ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Conversation #{detail?.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail?.visitor_user
                    ? `${detail.visitor_user.name} (${detail.visitor_user.email})`
                    : [detail?.visitor_name || "Visitor", detail?.visitor_email ? `(${detail.visitor_email})` : null]
                        .filter(Boolean)
                        .join(" ")}
                  {detail?.visitor_phone ? ` · ${detail.visitor_phone}` : ""}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" color={statusColor(detail?.status)} label={STATUS_LABELS[detail?.status] || detail?.status || "-"} />
                  {detail?.audience && detail.audience !== "public" ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${AUDIENCE_LABELS[detail.audience]} portal${detail.student_name ? ` · about ${detail.student_name}` : ""}`}
                    />
                  ) : null}
                  {detail?.escalation_reason === "safeguarding" ? <Chip size="small" color="error" label="SAFEGUARDING" /> : null}
                  {detail?.assigned_admin ? (
                    <Chip size="small" label={`Assigned: ${detail.assigned_admin.name}`} />
                  ) : (
                    <Chip size="small" variant="outlined" label="Unassigned" />
                  )}
                </Box>
                {detail?.escalation_reason ? (
                  <Typography variant="caption" color="warning.dark" sx={{ display: "block", mt: 1 }}>
                    Escalated{detail.escalated_at ? ` ${formatWhen(detail.escalated_at)}` : ""}: {detail.escalation_reason}
                  </Typography>
                ) : null}
              </Box>

              <Box ref={messagesBoxRef} sx={{ flex: 1, overflow: "auto", p: 2, maxHeight: 520 }}>
                {sortedMessages.map(renderMessage)}
                {detail && !(detail.messages || []).some((m) => m.sender_type === "visitor") ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
                    The visitor opened the chat but has not written anything.
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={assignToMe}
                    disabled={saving || detail?.status !== "queued_for_human"}
                  >
                    Assign to me
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={closeConversation}
                    disabled={saving || detail?.status === "closed" || !detail?.assigned_admin}
                  >
                    Close
                  </Button>
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Type your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  disabled={detail?.status === "closed"}
                  helperText={
                    detail?.status === "queued_for_human"
                      ? "Click \"Assign to me\" first; the visitor sees your replies in their chat."
                      : detail?.status === "bot"
                        ? "The bot is still handling this chat; it has not been escalated to the team."
                        : undefined
                  }
                />
                <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={sendReply}
                    disabled={saving || !reply.trim() || detail?.status === "closed"}
                  >
                    Send reply
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
